import React, { HTMLAttributes, Ref, useCallback } from 'react';
import { classNames } from '../../utilities';
import { RadioGroupContext } from '.';
import '../../base.scss';
import './Radio.scss';

export interface RadioProps extends HTMLAttributes<HTMLInputElement> {
  /** required for accessibility if no labels */
  'aria-label'?: string;
  /** Id that points to the label for the radio */
  'aria-labelledby'?: string;
  /* is radio button selected */
  checked?: boolean;
  /* Attach a custom class to the radio container */
  className?: string;
  /* A ref to access the component */
  componentRef?: Ref<HTMLInputElement>;
  /* Uncontrolled radio state, mutually exclusive with checked */
  defaultChecked?: boolean;
  /* Disable the input */
  disabled?: boolean;
  /* ID for the radio */
  id?: string;
  /* optional text to appear beside radio button */
  label?: React.ReactNode | string;
  /* key that defines the radio group. All radio buttons in the same group must share the same name */
  name?: string;
  /* optional value to assign to the input on form submission */
  value?: string | number; // TODO: remove the ?, should this be required?
  /* Callback handler for value changes */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, isChecked?: boolean) => void;
}

export function Radio(props: RadioProps) {
  const { className, checked, disabled, id, name, label, onChange } = props;
  const value = props.value ?? '';
  const radioGroup = React.useContext(RadioGroupContext);
  const groupOnChange = radioGroup.onChange;

  const onChangeWrapper = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (groupOnChange) {
      groupOnChange(event);
    }

    if (onChange) {
      onChange(event);
    }
  }, [onChange, groupOnChange]);

  const isDisabled = disabled ?? radioGroup.disabled;
  const actualName = name ?? radioGroup.name;

  const isChecked: boolean = (typeof(radioGroup.selectedValue) !== 'undefined') 
    ? radioGroup.selectedValue === value // value is set on radioGroup, always use this one if available
    : checked ?? false; // use local prop, or assume unchecked.

  const radioClassNames = classNames('ps-radio', className, isDisabled ? 'ps-radio--disabled' : '');

  return (
    <label className={radioClassNames}>
      <span className="ps-radio__circle">
        <input
          aria-label={props['aria-label']}
          aria-labelledby={props['aria-labelledby']}
          checked={isChecked}
          id={id}
          type="radio"
          disabled={isDisabled}
          onChange={onChangeWrapper}
          value={value}
          name={actualName}
          className="ps-radio__circle-input"
        />
        <span className="ps-radio__circle-icon" />
      </span>
      <span className="ps-radio__label">{label}</span>
    </label>
  );
}
